<template>
  <div v-if="isOpen" ref="modalRef" class="absolute h-0 w-0" @keydown.esc="closeModal">
    <div class="fixed inset-0 z-40 bg-gray-900 bg-opacity-50" />
    <div tabindex="-1" class="widget-confirm-modal">
      <Transition name="slide-up" appear>
        <div
          role="dialog"
          v-if="isOpen"
          class="relative flex h-full w-full flex-col justify-center px-3 md:h-auto"
          :class="`${modalSizeClasses[size]}`"
        >
          <!-- Modal content -->
          <div
            ref="modalContent"
            class="relative flex flex-col gap-y-6 bg-background p-12 shadow-custom"
            :class="[{ 'md:rounded-custom': size !== 'full' }, modalSizeClasses[size]]"
          >
            <!-- Modal header -->
            <div v-if="$slots.header" class="widget-confirm-modal_header">
              <slot name="header" />
              <WButton v-if="!hideClose" type="ghost" class="widget-confirm-modal_close_btn" @click="closeModal">
                <slot name="close-icon">
                  <WidgetIcon :icon="themeIcons.close" height="1.25rem" class="widget-secondary-icon" />
                </slot>
              </WButton>
            </div>
            <!-- Modal body -->
            <div class="confirm-modal-body overflow-y-auto" :class="[$slots.header ? '' : 'pt-0', bodyClass]">
              <slot />
            </div>
            <!-- Modal footer -->
            <div v-if="$slots.footer" class="rounded-b-custom">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, nextTick, ref, watch } from 'vue';
  import { themeIcons } from 'src/utils/config';
  import { useScrollLock } from 'src/composables/useScrollLock';
  import WidgetIcon from './WidgetIcon.vue';
  import WButton from './Button.vue';

  const props = withDefaults(
    defineProps<{
      modelValue: boolean;
      hideClose: boolean;
      size?: keyof typeof modalSizeClasses;
      bodyClass?: string;
    }>(),
    {
      size: 'md',
      modelValue: false,
      hideClose: false,
      bodyClass: '',
    }
  );

  const emit = defineEmits<{
    (event: 'close'): void;
    (event: 'update:modelValue', isOpen: boolean): void;
  }>();
  const isOpen = computed({
    get() {
      return props.modelValue;
    },
    set(value) {
      emit('update:modelValue', value);
    },
  });
  const modalContent = ref<HTMLDivElement | null>(null);
  const modalRef = ref<HTMLElement>();

  useScrollLock(isOpen, modalRef.value);
  watch(
    isOpen,
    (value) => {
      if (typeof window === 'undefined') return;
      nextTick(() => {
        if (value) {
          if (!modalContent.value) return;
          modalContent.value.tabIndex = 1;
          modalContent.value.focus();
        }
      });
    },
    { immediate: true }
  );

  const modalSizeClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'w-screen h-screen !p-0',
  };
  function closeModal() {
    emit('close');
    isOpen.value = false;
  }
</script>
